/** @jsx jsx */
import { jsx } from "frontend/src/utils/jsx"
import { Grid, SectionGrid } from "frontend/src/components/Layout"
import { Fragment } from "react"

function NutritionInfoLeft({ title, list }) {
  return (
    <section sx={{ gridColumn: 1 }}>
      <h3
        sx={{
          variant: "text.h5",
          textTransform: "uppercase",
          color: "almostBlack",
        }}
      >
        {title}
      </h3>
      <div
        sx={{
          width: "100%",
          maxWidth: "100%",
          variant: "text.body2",
        }}
      >
        {list[0].children[0].text}
      </div>
    </section>
  )
}

function NutritionTable({ title, product, col2, col3 }) {
  const { naehrwertePerHundred = {}, portionSize = undefined, naehrwertePerPortion = {} } = product
  const {
    energyKcal: KcalHundred,
    energyKj: KjHundred,
    fat: fatHundred,
    saturatedFat: saturatedHundred,
    carbs: carbsHundred,
    sugar: sugarHundred,
    protein: proteinHundred,
    fiber: fiberHundred,
    salt: saltHundred,
  } = naehrwertePerHundred
  const {
    energyKcal: KcalPortion = undefined,
    energyKj: KjPortion = undefined,
    fat: fatPortion = undefined,
    saturatedFat: saturatedPortion = undefined,
    carbs: carbsPortion = undefined,
    sugar: sugarPortion = undefined,
    protein: proteinPortion = undefined,
    fiber: fiberPortion = undefined,
    salt: saltPortion = undefined,
  } = naehrwertePerPortion
  const columns = portionSize != null ? "4fr 2fr 2fr" : "4fr 2fr"
  const lineMax = portionSize != null ? 4 : 3
  return (
    <Grid
      columns={columns}
      sx={{
        gridColumn: ["1", "1", "2 / 5", "2 / 5"],
        width: "100%",
        marginBottom: "25px",
        fontFamily: "body",
      }}
    >
      {/* Table heading */}
      <div
        sx={{
          variant: "text.body1",
          textTransform: "none",
          color: "almostBlack",
        }}
      >
        {title}
      </div>
      <div
        sx={{
          variant: "text.body1",
          textTransform: "none",
          color: "almostBlack",
          textAlign: "center",
        }}
      >
        {col2}
      </div>
      {portionSize && (
        <div
          sx={{
            variant: "text.body1",
            textTransform: "none",
            color: "almostBlack",
            textAlign: "center",
          }}
        >
          {col3 && col3 !== "" && col3}
        </div>
      )}
      <Line max={lineMax} margin="10px" />
      {/* first row: Energy I */}
      <NutritionTableRow
        description="Energie"
        hundred={KcalHundred}
        portion={KcalPortion}
        unit="kcal"
      />
      {/* second row: Energy II */}
      <NutritionTableRow description="" hundred={KjHundred} portion={KjPortion} unit="kJ" />
      <Line max={lineMax} margin="10px" />
      {/* third row: Fat I */}
      <NutritionTableRow description="Fett" hundred={fatHundred} portion={fatPortion} unit="g" />
      {/* fourth row: Fat II */}
      <NutritionTableRow
        description="(davon gesättigte Fettsäuren)"
        hundred={saturatedHundred}
        portion={saturatedPortion}
        unit="g"
      />
      <Line max={lineMax} margin="10px" />
      {/* fifth row: Carbohydrates I */}
      <NutritionTableRow
        description="Kohlenhydrate"
        hundred={carbsHundred}
        portion={carbsPortion}
        unit="g"
      />
      {/* sixth row: Carbohydrates II */}
      <NutritionTableRow
        description="(davon Zucker)"
        hundred={sugarHundred}
        portion={sugarPortion}
        unit="g"
      />
      <Line max={lineMax} margin="10px" />
      {/* seventh row: Fibers */}
      <NutritionTableRow
        description="Ballaststoffe"
        hundred={fiberHundred}
        portion={fiberPortion}
        unit="g"
      />
      <Line max={lineMax} margin="10px" />
      {/* eighth row: Proteins */}
      <NutritionTableRow
        description="Protein"
        hundred={proteinHundred}
        portion={proteinPortion}
        unit="g"
      />
      <Line max={lineMax} margin="10px" />
      {/* ninth row: Salt */}
      <NutritionTableRow description="Salz" hundred={saltHundred} portion={saltPortion} unit="g" />
    </Grid>
  )
}

function NutritionTableRow({
  description = "",
  hundred = undefined,
  portion = undefined,
  unit = "",
}) {
  return (
    <Fragment>
      <div
        sx={{
          variant: "text.body2",
          color: "almostBlack",
          pl: "5px",
        }}
      >
        {description}
      </div>
      <div
        sx={{
          variant: "text.body2",
          color: "almostBlack",
          textAlign: "center",
        }}
      >
        {hundred} {unit}
      </div>
      {portion != null && (
        <div
          sx={{
            variant: "text.body2",
            color: "almostBlack",
            textAlign: "right",
            pr: "10px",
          }}
        >
          {portion} {unit}
        </div>
      )}
    </Fragment>
  )
}

function NutritionActive({ title, active, message }) {
  return (
    <Grid
      columns="4fr 2fr 1fr"
      sx={{
        gridColumn: ["1", "1", "2 / 5", "2 / 5"],
        width: "100%",
        fontFamily: "body",
      }}
    >
      {/* Table heading */}
      <div
        sx={{
          variant: "text.body1",
          textTransform: "none",
          color: "almostBlack",
          gridColumn: "1 / 3",
          fontFamily: "heading",
        }}
      >
        {title}
      </div>
      <div
        sx={{
          variant: "text.body1",
          textTransform: "none",
          color: "almostBlack",
          gridColumn: "3 / 4",
          fontFamily: "heading",
          textAlign: "right",
        }}
      >
        %NRV
      </div>
      <Line max={4} margin="10px" />
      {active.map((ingredient, index) => {
        const { amountPerServing: amount, euReference, nrvPercent } = ingredient
        return (
          <Fragment key={index}>
            <div
              sx={{
                variant: "text.body2",
                color: "almostBlack",
                pl: "5px",
              }}
            >
              {ingredient.name}
            </div>
            <div
              sx={{
                variant: "text.body2",
                color: "almostBlack",
                textAlign: "right",
              }}
            >
              {amount}
              {!euReference && <span>*</span>}
            </div>
            <div
              sx={{
                variant: "text.body2",
                color: "almostBlack",
                textAlign: "right",
                pr: "5px",
              }}
            >
              {nrvPercent && nrvPercent > 0 ? (
                <span>
                  {nrvPercent}
                  {euReference && "%"}
                </span>
              ) : (
                <span>――</span>
              )}
            </div>
            <Line max={4} margin="10px" />
          </Fragment>
        )
      })}
      <div
        sx={{
          variant: "text.body2",
          color: "almostBlack",
          gridColumn: "1 / 3",
          pl: "5px",
        }}
      >
        {message}
      </div>
    </Grid>
  )
}

function Line({ max, margin }) {
  if (margin) {
    return (
      <div
        sx={{
          height: ["1px", "0.38px", "0.6px", "1px"],
          width: "100%",
          borderColor: "almostBlack",
          borderStyle: "solid",
          borderWidth: ["1px", "0.384px", "0.6px", "1px"],
          gridColumn: `1 / ${max}`,
          marginY: `${margin}`,
        }}
      />
    )
  }
  return (
    <div
      sx={{
        height: ["1px", "0.38px", "0.6px", "1px"],
        width: "100%",
        borderColor: "almostBlack",
        borderStyle: "solid",
        borderWidth: ["1px", "0.384px", "0.6px", "1px"],
        gridColumn: `1 / ${max}`,
      }}
    />
  )
}

function NutritionInfo({
  describedProduct,
  messageReferenceAmount,
  titleActiveIngredients,
  titleIngredientList,
  titleNutritionTable,
  titleNutTableCol2,
  titleNutTableCol3,
  titleProductDescription,
  toggleIngredientList,
  toggleNutritionTable,
  toggleActiveList,
  toggleActiveTable,
}) {
  const { activeIngredientsList = [], ingredientList } = describedProduct
  const activeIngredientsRaw = activeIngredientsList.map(ingredient => ingredient.name).join(", ")
  const activeIngredients = [
    {
      children: [
        {
          text: activeIngredientsRaw,
        },
      ],
    },
  ]
  const toggles = [toggleIngredientList, toggleNutritionTable, toggleActiveList, toggleActiveTable]
  const toggleValue = toggles.reduce((accumulator, toggle, index) => {
    if (toggle) {
      return accumulator + 2 ** index
    } else {
      return accumulator
    }
  }, 0)
  if (toggleValue === 1) {
    return (
      <SectionGrid sx={{ my: "25px", width: ["90%", "90%", "unset"], mx: "auto" }}>
        <SectionGrid.narrow>
          <Grid columns={"1fr"}>
            <NutritionInfoLeft title={titleIngredientList} list={ingredientList} />
          </Grid>
        </SectionGrid.narrow>
      </SectionGrid>
    )
  } else if (toggleValue === 3) {
    return (
      <SectionGrid sx={{ my: "25px", width: ["90%", "90%", "unset"], mx: "auto" }}>
        <SectionGrid.narrow>
          <Grid columns={["1fr", "1fr", "repeat(1,1.5fr 2fr)", "repeat(1,1.5fr 2fr)"]} gap={"20px"}>
            <NutritionInfoLeft title={titleIngredientList} list={ingredientList} />
            <NutritionTable
              title={titleNutritionTable}
              product={describedProduct}
              col2={titleNutTableCol2}
              col3={titleNutTableCol3}
            />
          </Grid>
        </SectionGrid.narrow>
      </SectionGrid>
    )
  } else if (toggleValue === 9) {
    return (
      <SectionGrid sx={{ my: "25px", width: ["90%", "90%", "unset"], mx: "auto" }}>
        <SectionGrid.narrow>
          <Grid columns={["1fr", "1fr", "repeat(1,1.5fr 2fr)", "repeat(1,1.5fr 2fr)"]} gap={"20px"}>
            <NutritionInfoLeft title={titleIngredientList} list={ingredientList} />
            <NutritionActive
              title={titleActiveIngredients}
              active={activeIngredientsList}
              message={messageReferenceAmount}
            />
          </Grid>
        </SectionGrid.narrow>
      </SectionGrid>
    )
  }
  return (
    <SectionGrid sx={{ my: "25px", width: ["90%", "90%", "unset"], mx: "auto" }}>
      <SectionGrid.narrow>
        <Grid columns={["1fr", "1fr", "repeat(1,1.5fr 2fr)", "repeat(1,1.5fr 2fr)"]} gap={"20px"}>
          <NutritionInfoLeft title={titleIngredientList} list={ingredientList} />
          {toggleNutritionTable && (
            <NutritionTable
              title={titleNutritionTable}
              product={describedProduct}
              col2={titleNutTableCol2}
              col3={titleNutTableCol3}
            />
          )}
          {toggleActiveList && (
            <NutritionInfoLeft title={titleProductDescription} list={activeIngredients} />
          )}
          {toggleActiveTable && (
            <NutritionActive
              title={titleActiveIngredients}
              active={activeIngredientsList}
              message={messageReferenceAmount}
            />
          )}
        </Grid>
      </SectionGrid.narrow>
    </SectionGrid>
  )
}

export { NutritionInfo }
